
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { SwapRequest } from "./types";

// Fetch all swap requests - both sent and received
export const fetchAllUserSwapRequests = async (): Promise<SwapRequest[]> => {
  try {
    console.log("Fetching all user swap requests (sent and received)...");
    
    // Get current user
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError) {
      console.error("Session error:", sessionError);
      toast.error("Error getting session");
      return [];
    }
    
    if (!sessionData.session) {
      console.log("No session found when fetching swap requests");
      toast.error("You must be logged in to view swap requests");
      return [];
    }

    const userId = sessionData.session.user.id;
    console.log("Fetching all swap requests for user:", userId);
    
    // First get the user's reagent IDs (for received requests)
    const { data: userReagents, error: reagentsError } = await supabase
      .from("reagents")
      .select("id")
      .eq("user_id", userId);
      
    if (reagentsError) {
      console.error("Error fetching user's reagents:", reagentsError);
      return [];
    }
    
    const reagentIds = userReagents?.map(reagent => reagent.id) || [];
    console.log("User reagents found:", reagentIds.length);
    
    // Create an OR filter to get both:
    // 1. Requests for the user's reagents (received requests)
    // 2. Requests made by the user (sent requests)
    // Make sure we're not filtering out cancelled requests
    const { data, error } = await supabase
      .from("swap_requests")
      .select(`
        id,
        reagent_id,
        requester_id,
        status,
        credit_offer,
        offered_reagent_id,
        created_at,
        updated_at,
        reagents!reagent_id(
          id,
          name,
          user_id,
          catalogue_number,
          amount,
          amount_unit,
          storage_conditions,
          image_url
        ),
        profiles!requester_id(
          username,
          lab,
          university
        ),
        offered_reagents(
          id,
          name,
          amount,
          amount_unit,
          catalogue_number,
          description,
          storage_conditions
        )
      `)
      .or(`reagent_id.in.(${reagentIds.join(',')}),requester_id.eq.${userId}`)
      .order("updated_at", { ascending: false });

    if (error) {
      console.error("Error fetching all user swap requests:", error);
      toast.error("Failed to load swap requests");
      return [];
    }
    
    console.log("Raw swap requests data (all):", data?.length || 0, "requests found");
    
    if (!data || data.length === 0) {
      console.log("No swap requests found for this user");
      return [];
    }
    
    // If there are offered_reagent_ids, fetch them separately
    const offeredReagentIds = data
      .filter(item => item.offered_reagent_id)
      .map(item => item.offered_reagent_id as string);
    
    let offeredReagentsMap: Record<string, any> = {};
    
    if (offeredReagentIds.length > 0) {
      const { data: offeredReagents, error: offeredError } = await supabase
        .from("reagents")
        .select("id, name, image_url")
        .in("id", offeredReagentIds);
        
      if (!offeredError && offeredReagents) {
        offeredReagentsMap = offeredReagents.reduce((acc, reagent) => {
          acc[reagent.id] = reagent;
          return acc;
        }, {} as Record<string, any>);
      }
    }
    
    // Transform data to match the SwapRequest type requirements
    // Include cancelled requests in the result
    const swapRequests: SwapRequest[] = data.map(item => {
      // Handle offered_reagent separately instead of through the join
      const offeredReagent = item.offered_reagent_id 
        ? offeredReagentsMap[item.offered_reagent_id] || null
        : null;
        
      return {
        ...item,
        // Ensure all required properties are present
        reagents: item.reagents || {
          id: "",
          name: "Unknown Reagent",
          user_id: userId,
          image_url: null
        },
        profiles: item.profiles || {
          username: "Unknown User",
          lab: null,
          university: null
        },
        offered_reagents: item.offered_reagents || [],
        // Use our manually fetched offered_reagent
        offered_reagent: offeredReagent
      };
    });
    
    console.log("All transformed swap requests:", swapRequests.length, "items");
    
    // Classify each request as sent or received and log offered reagent info
    swapRequests.forEach(req => {
      const isSent = req.requester_id === userId;
      console.log(`Swap request: ID=${req.id}, ${isSent ? 'SENT' : 'RECEIVED'}, Status=${req.status}, Reagent=${req.reagents.name}, Offered Reagent ID=${req.offered_reagent_id}`);
      if (req.offered_reagent) {
        console.log(`Offered reagent: ${req.offered_reagent.name} (${req.offered_reagent.id})`);
      }
    });
    
    return swapRequests;
  } catch (error) {
    console.error("Exception in fetchAllUserSwapRequests:", error);
    toast.error("An unexpected error occurred");
    return [];
  }
};
